import { ApiOperationType, AsoScenarioScopeResponse } from '@ydistri/api-sdk';
import { createDebugLog } from '@ydistri/utils';
import {
  SignalAsoScenarioScopeChanged,
  SignalAsoScenarioScopeUpdate,
} from '../../signalr/signalrInterfaces';
import { signalrClient } from '../../signalr/client';
import { AsoCollection } from '../../swagger/collections';

const SCOPE_CREATED = 'ScopeCreated';
const SCOPE_DELETED = 'ScopeDeleted';
const SCOPE_PRIORITY = 'ScopePriority';
const SCOPE_SOURCE_CREATED = 'ScopeSourceCreated';
const SCOPE_SOURCE_SCOPE_ID = 'ScopeSourceScopeId';
const SCOPE_SOURCE_IS_FORCED = 'ScopeSourceIsForced';
const SCOPE_SOURCE_IS_FORCED_TO_ZERO = 'ScopeSourceIsForcedToZero';
const SCOPE_TARGET_CREATED = 'ScopeTargetCreated';
const SCOPE_TARGET_PRIORITY = 'ScopeTargetPriority';
const SCOPE_TARGET_SCOPE_ID = 'ScopeTargetScopeId';
const SCOPE_TARGET_DELETED = 'ScopeTargetDeleted';

const debugLog = createDebugLog('Aso', 'asoScenarioScopeChangeListener');

interface AsoScopesCacheEntryApi {
  cacheDataLoaded: Promise<unknown>;
  cacheEntryRemoved: Promise<unknown>;
  updateCachedData: (recipe: (draft: AsoScenarioScopeResponse[]) => void) => void;
}

const isSafeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value);

const getMaxPriority = (items: { priority: number }[]): number =>
  items.reduce((max, item) => Math.max(max, item.priority), 0);

const normalizePriorities = (items: { priority: number }[]) => {
  [...items]
    .sort((a, b) => a.priority - b.priority)
    .forEach((item, index) => {
      item.priority = index + 1;
    });
};

const moveItemToPriority = (
  items: { id: number; priority: number }[],
  itemId: number,
  priority: number,
) => {
  const ordered = [...items].sort((a, b) => a.priority - b.priority);
  const currentIndex = ordered.findIndex(item => item.id === itemId);
  if (currentIndex < 0) return;

  const [item] = ordered.splice(currentIndex, 1);
  const targetIndex = Math.max(0, Math.min(priority - 1, ordered.length));
  ordered.splice(targetIndex, 0, item);

  ordered.forEach((orderedItem, index) => {
    orderedItem.priority = index + 1;
  });
};

const findScope = (
  scopes: AsoScenarioScopeResponse[],
  scopeId: number,
): AsoScenarioScopeResponse | undefined => scopes.find(scope => scope.id === scopeId);

export const upsertAsoScope = (
  scopes: AsoScenarioScopeResponse[],
  nextScope: AsoScenarioScopeResponse,
): void => {
  const existingIndex = scopes.findIndex(scope => scope.id === nextScope.id);
  if (existingIndex >= 0) {
    scopes[existingIndex] = nextScope;
  } else {
    scopes.push(nextScope);
  }
};

export const removeAsoScope = (scopes: AsoScenarioScopeResponse[], scopeId: number): void => {
  const scopeIndex = scopes.findIndex(scope => scope.id === scopeId);
  if (scopeIndex < 0) return;

  scopes.splice(scopeIndex, 1);
  normalizePriorities(scopes);
};

export const setAsoScopeSource = (
  scopes: AsoScenarioScopeResponse[],
  scopeId: number,
  source: AsoScenarioScopeResponse['source'],
): void => {
  const scope = findScope(scopes, scopeId);
  if (!scope) return;

  scope.source = source;
};

export const upsertAsoScopeTarget = (
  scopes: AsoScenarioScopeResponse[],
  scopeId: number,
  nextTarget: AsoScenarioScopeResponse['targets'][number],
): void => {
  const scope = findScope(scopes, scopeId);
  if (!scope) return;

  const existingIndex = scope.targets.findIndex(target => target.id === nextTarget.id);
  if (existingIndex >= 0) {
    scope.targets[existingIndex] = nextTarget;
  } else {
    scope.targets.push(nextTarget);
  }
};

export const removeAsoScopeTarget = (
  scopes: AsoScenarioScopeResponse[],
  scopeId: number,
  targetId: number,
): void => {
  const scope = findScope(scopes, scopeId);
  if (!scope) return;

  const targetIndex = scope.targets.findIndex(target => target.id === targetId);
  if (targetIndex < 0) return;

  scope.targets.splice(targetIndex, 1);
  normalizePriorities(scope.targets);
};

export const shouldReloadAsoScopesFromSignalr = (
  updates: SignalAsoScenarioScopeUpdate[],
): boolean => updates.some(update => update.fieldName === SCOPE_CREATED);

export const applyAsoScopeSignalrUpdate = (
  scopes: AsoScenarioScopeResponse[],
  update: SignalAsoScenarioScopeUpdate,
): void => {
  if (!isSafeInteger(update.scopeId)) return;

  switch (update.fieldName) {
    case SCOPE_CREATED: {
      return;
    }

    case SCOPE_DELETED: {
      removeAsoScope(scopes, update.scopeId);
      return;
    }

    case SCOPE_PRIORITY: {
      if (!isSafeInteger(update.priority)) return;

      moveItemToPriority(scopes, update.scopeId, update.priority);
      return;
    }

    case SCOPE_SOURCE_CREATED: {
      const scope = findScope(scopes, update.scopeId);
      if (!scope || !isSafeInteger(update.scopeSourceId)) return;

      scope.source = {
        id: update.scopeSourceId,
        scopeId: update.selectedScopeId ?? null,
        isForced: typeof update.isForced === 'boolean' ? update.isForced : scope.source.isForced,
        isForcedToZero:
          typeof update.isForcedToZero === 'boolean'
            ? update.isForcedToZero
            : scope.source.isForcedToZero,
      };
      return;
    }

    case SCOPE_SOURCE_SCOPE_ID: {
      const scope = findScope(scopes, update.scopeId);
      if (!scope) return;

      if (isSafeInteger(update.scopeSourceId)) {
        scope.source.id = update.scopeSourceId;
      }
      scope.source.scopeId = update.selectedScopeId ?? null;
      return;
    }

    case SCOPE_SOURCE_IS_FORCED: {
      const scope = findScope(scopes, update.scopeId);
      if (!scope || typeof update.isForced !== 'boolean') return;

      if (isSafeInteger(update.scopeSourceId)) {
        scope.source.id = update.scopeSourceId;
      }
      scope.source.isForced = update.isForced;
      return;
    }

    case SCOPE_SOURCE_IS_FORCED_TO_ZERO: {
      const scope = findScope(scopes, update.scopeId);
      if (!scope || typeof update.isForcedToZero !== 'boolean') return;

      if (isSafeInteger(update.scopeSourceId)) {
        scope.source.id = update.scopeSourceId;
      }
      scope.source.isForcedToZero = update.isForcedToZero;
      return;
    }

    case SCOPE_TARGET_CREATED: {
      const scope = findScope(scopes, update.scopeId);
      if (
        !scope ||
        !isSafeInteger(update.scopeTargetId) ||
        scope.targets.some(target => target.id === update.scopeTargetId)
      ) {
        return;
      }

      const priority = isSafeInteger(update.priority)
        ? update.priority
        : getMaxPriority(scope.targets) + 1;
      scope.targets.push({
        id: update.scopeTargetId,
        scopeId: update.selectedScopeId ?? null,
        priority,
      });
      return;
    }

    case SCOPE_TARGET_PRIORITY: {
      if (!isSafeInteger(update.scopeTargetId) || !isSafeInteger(update.priority)) return;

      const scope = findScope(scopes, update.scopeId);
      if (!scope) return;

      moveItemToPriority(scope.targets, update.scopeTargetId, update.priority);
      return;
    }

    case SCOPE_TARGET_SCOPE_ID: {
      if (!isSafeInteger(update.scopeTargetId)) return;

      const scope = findScope(scopes, update.scopeId);
      const target = scope?.targets.find(item => item.id === update.scopeTargetId);
      if (!target) return;

      target.scopeId = update.selectedScopeId ?? null;
      return;
    }

    case SCOPE_TARGET_DELETED: {
      if (!isSafeInteger(update.scopeTargetId)) return;

      removeAsoScopeTarget(scopes, update.scopeId, update.scopeTargetId);
      return;
    }
  }
};

export const applyAsoScopeSignalrUpdates = (
  scopes: AsoScenarioScopeResponse[],
  updates: SignalAsoScenarioScopeUpdate[],
): void => {
  updates.forEach(update => applyAsoScopeSignalrUpdate(scopes, update));
};

export const handleAsoScenarioScopeChangeListener = async (
  asoScenarioId: number,
  projectShortName: string | undefined,
  api: AsoScopesCacheEntryApi,
): Promise<void> => {
  let isCacheActive = true;

  try {
    await api.cacheDataLoaded;

    const reloadScopes = () => {
      AsoCollection.scenariosScopesList(asoScenarioId)
        .then(({ data }) => {
          if (!isCacheActive) return;

          api.updateCachedData(draft => {
            draft.splice(0, draft.length, ...data.data);
          });
        })
        .catch((error: unknown) => {
          debugLog('Failed to reload ASO scopes after SignalR create event', error);
        });
    };

    const listener = (signal: SignalAsoScenarioScopeChanged) => {
      if (
        signal.asoScenarioId !== asoScenarioId ||
        !projectShortName ||
        signal.projectSlug.toLocaleLowerCase() !== projectShortName.toLocaleLowerCase() ||
        signal.operationType !== ApiOperationType.Patch
      ) {
        return;
      }

      if (shouldReloadAsoScopesFromSignalr(signal.updates)) {
        reloadScopes();
        return;
      }

      api.updateCachedData(draft => {
        applyAsoScopeSignalrUpdates(draft, signal.updates);
      });
    };

    signalrClient.on('AsoScenarioScopeChanged', listener);

    await api.cacheEntryRemoved;
    isCacheActive = false;
    signalrClient.off('AsoScenarioScopeChanged', listener);
  } catch {
    isCacheActive = false;
    // no-op in case cache entry was removed before data was loaded
  }
};
